import { useEffect, useState } from "react";
import { Sparkle, ArrowsClockwise, CaretDown, CaretUp } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";

function renderLine(line, i) {
  const parts = line.split(/\*\*(.+?)\*\*/g);
  const isBullet = line.trim().startsWith("- ");
  return (
    <div key={i} className={isBullet ? "pl-3 text-sm text-zinc-300" : "text-sm text-zinc-300"}>
      {parts.map((p, j) =>
        j % 2 === 1 ? (
          <strong key={j} className="text-brand-lighter tracking-wide block mt-3 mb-1">{p}</strong>
        ) : (
          <span key={j}>{p}</span>
        )
      )}
    </div>
  );
}

export default function WeeklyDigestCard() {
  const [digest, setDigest] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/digest/latest");
        setDigest(data.digest);
      } catch (_e) {}
      finally { setLoaded(true); }
    })();
  }, []);

  const generate = async (force) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/digest/generate${force ? "?force=true" : ""}`);
      setDigest(data);
      setOpen(true);
      toast.success("Weekly digest ready");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Digest generation failed");
    } finally {
      setBusy(false);
    }
  };

  if (!loaded) return null;

  const weekLabel = digest
    ? new Date(`${digest.week_of}T12:00:00Z`).toLocaleDateString(undefined, { day: "numeric", month: "short" })
    : null;

  return (
    <section className="card-flat p-6 border-brand/30" data-testid="weekly-digest-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Sparkle size={18} weight="fill" className="text-brand-light" />
          <div>
            <div className="font-display text-xl leading-none">Weekly digest</div>
            {digest && <div className="tracker text-zinc-500 text-[10px] mt-1">WEEK OF {weekLabel} · AI SUMMARY</div>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {digest ? (
            <>
              <button onClick={() => generate(true)} disabled={busy} className="btn-ghost !py-1.5 !px-3 !text-[10px] disabled:opacity-40" data-testid="digest-regenerate">
                <ArrowsClockwise size={12} weight="bold" className={busy ? "animate-spin" : ""} /> {busy ? "Working…" : "Regenerate"}
              </button>
              <button onClick={() => setOpen((o) => !o)} className="btn-ghost !py-1.5 !px-3 !text-[10px]" data-testid="digest-toggle">
                {open ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}
              </button>
            </>
          ) : (
            <button onClick={() => generate(false)} disabled={busy} className="btn-primary !py-2 !px-4 !text-xs disabled:opacity-40" data-testid="digest-generate">
              <Sparkle size={12} weight="fill" className={busy ? "animate-pulse" : ""} /> {busy ? "Analysing your week…" : "Generate digest"}
            </button>
          )}
        </div>
      </div>

      {digest && open && (
        <div className="mt-4 pt-4 border-t border-white/10" data-testid="digest-content">
          {digest.content.split("\n").filter((l) => l.trim()).map(renderLine)}
        </div>
      )}
      {!digest && (
        <div className="text-xs text-zinc-500 mt-3">
          Claude summarises last week — money collected, sessions performed & missed, check-ins, habit adherence and who needs a nudge. Auto-lands every Monday.
        </div>
      )}
    </section>
  );
}
